import { publicAxiosInstance } from "@/axiosInstance";
import { useCompanySettings } from "@/hooks/useCompanySettings";
import useTranslation from "@/hooks/useTranslation";
import { useUserSettings } from "@/hooks/useUserSettings";
import { DownloadOutlined } from "@ant-design/icons";
import { Button, message, Spin, Tooltip } from "antd";
import React, { ReactNode, useState } from "react";
import ActionsPopOverItem from "../ActionsPopOver/ActionsPopOverItem";
import FakeLink from "../FakeLink/FakeLink";
import { downloadFile } from "./downloadFile";

export interface FileDownloaderProps {
  fileUrlFn: () => Promise<string>;
  fileOutputName?: string;
  buttonText?: string | ReactNode;
  disabled?: boolean;
  tooltipText?: string;
  icon?: React.ReactNode;
  type?: "button" | "text" | "action" | "link";
  onDownload?: () => void;
  onFinish?: () => void;
  customRender?: (props: { onClick: () => void; loading?: boolean }) => React.ReactNode;
  addTimestampAndCompany?: boolean;
}

function prependTimestamp(fileName: string): string {
  const date = new Date();
  // ensure that numbers like months, days, hours, or minutes always appear with two digits
  const prependZero = (n: number) => n.toString().padStart(2, "0");

  const year = date.getFullYear();
  const month = prependZero(date.getMonth() + 1); // Months are 0-based
  const day = prependZero(date.getDate());
  const hour = prependZero(date.getHours());
  const minute = prependZero(date.getMinutes());

  const timestamp = `${year}${month}${day}${hour}${minute}`;
  return `${timestamp}-${fileName}`;
}

function prependCompanyName(fileName: string, companyName: string): string {
  return `${companyName.replace(/[^A-Za-z1-3]/g, "")}-${fileName}`;
}

export default function FileDownloader({
  fileUrlFn,
  fileOutputName,
  buttonText,
  disabled,
  tooltipText,
  icon,
  type,
  onDownload,
  onFinish,
  customRender,
  addTimestampAndCompany,
}: FileDownloaderProps) {
  const { data: userSettings } = useUserSettings();
  const { data: companySettings } = useCompanySettings();
  const [fileIsDownloading, setFileIsDownloading] = useState(false);
  const translation = useTranslation();

  const fileDownloadingHandler = async () => {
    setFileIsDownloading(true);
    onDownload && onDownload();

    if (addTimestampAndCompany && fileOutputName) {
      fileOutputName = prependTimestamp(fileOutputName);
    }

    // company name only added when user is root-admin
    if (
      addTimestampAndCompany &&
      fileOutputName &&
      userSettings?.role === "root_admin" &&
      companySettings?.company_name
    ) {
      fileOutputName = prependCompanyName(fileOutputName, companySettings.company_name);
    }

    try {
      const url = await fileUrlFn();
      publicAxiosInstance
        .get(url, {
          responseType: "blob",
        })
        .then((response) => {
          downloadFile(url, response.data, fileOutputName);
          setFileIsDownloading(false);
          onFinish?.();
        });
    } catch {
      setFileIsDownloading(false);
      message.error(translation.errors.general);
    }
  };

  if (customRender) {
    return customRender({ onClick: fileDownloadingHandler, loading: fileIsDownloading });
  }

  return (
    <Tooltip
      placement="top"
      title={tooltipText}
    >
      <>
        {type === "text" ? (
          <span onClick={fileDownloadingHandler}>
            {fileIsDownloading && (
              <Spin
                size="small"
                style={{ margin: "0 3px" }}
              />
            )}
            {buttonText}
          </span>
        ) : type === "link" ? (
          <FakeLink onClick={fileDownloadingHandler}>
            {fileIsDownloading && (
              <Spin
                size="small"
                style={{ margin: "0 3px" }}
              />
            )}
            {buttonText}
          </FakeLink>
        ) : type === "action" ? (
          <ActionsPopOverItem
            onClick={fileDownloadingHandler}
            icon={icon || <DownloadOutlined />}
            loading={fileIsDownloading}
          >
            {buttonText || "Download template"}
          </ActionsPopOverItem>
        ) : (
          <Button
            icon={icon}
            onClick={fileDownloadingHandler}
            loading={fileIsDownloading}
            disabled={fileIsDownloading || disabled}
          >
            {buttonText}
          </Button>
        )}
      </>
    </Tooltip>
  );
}
